import { centsToEuros } from '@/constants'
import { Glyphs } from '@/ui/rapid-ui/glyphs'
import { Tooltip } from '@/ui/rapid-ui/tooltip'
import i18next from 'i18next'
import { getLocale } from '../../../hooks/helpers'
import type { DashboardCampaignModel } from './dashboard.vm'

export function BudgetWithBreakdown({
	budget,
	budgetBreakdown,
	className = '',
}: Pick<DashboardCampaignModel, 'budget' | 'budgetBreakdown'> & { className?: string }) {
	const hasBreakdown = budgetBreakdown
		&& budget !== null
		&& budgetBreakdown.metaBudget !== budget

	if (!hasBreakdown) {
		return <span className={className}>{formatCampaignBudget(budget)}</span>
	}

	return (
		<Tooltip
			content={
				<div className="space-y-1 py-0.5">
					<div className="flex items-center justify-between gap-4">
						<span className="text-grey-500">{i18next.t('dashboard.adSpend')}</span>
						<span className="font-medium">{formatCampaignBudget(budgetBreakdown.metaBudget)}</span>
					</div>
					<div className="flex items-center justify-between gap-4">
						<span className="text-grey-500">{i18next.t('dashboard.serviceFee')}</span>
						<span className="font-medium">{formatCampaignBudget(budgetBreakdown.serviceFee)}</span>
					</div>
				</div>
			}
		>
			<span className={`${className} flex items-center gap-1 cursor-default`}>
				{formatCampaignBudget(budget)}
				<span className="size-2.5 text-grey-400">
					<Glyphs.micro.InfoIcon />
				</span>
			</span>
		</Tooltip>
	)
}

export function formatCampaignBudget(budget: number | null): string {
	if (budget === null || !Number.isFinite(budget) || budget <= 0) {
		return i18next.t('dashboard.budgetNotSet')
	}

	const euros = centsToEuros(Math.round(budget))
	return new Intl.NumberFormat(getLocale(), {
		style: 'currency',
		currency: 'EUR',
		maximumFractionDigits: 2,
	}).format(euros)
}

export function formatCampaignDateRange(startedAt: Date | null, endsAt: Date | null): string {
	const formatter = new Intl.DateTimeFormat(getLocale(), {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	})

	if (startedAt && endsAt) {
		return `${formatter.format(startedAt)} - ${formatter.format(endsAt)}`
	}

	if (startedAt) {
		return i18next.t('dashboard.startsDate', { date: formatter.format(startedAt) })
	}

	if (endsAt) {
		return i18next.t('dashboard.endsDate', { date: formatter.format(endsAt) })
	}

	return i18next.t('dashboard.datesNotSet')
}
